import { isNil } from 'lodash';
import { CellData, CellTypeObject, CellTypeObjectWithAuto } from '../../../../../../common/table/TableTypes';
import { CellTypeConstants, CellTypeNames, FormatOptions } from '../../../../../../common/table/CellTypeConstants';
import { isFormulaCell } from '../../../../../../common/table/utils/tableCellDataPropertyUtils';
import { isFormula } from '../formula/tableFormulaPredicateUtils';

type CellTypeFormatOptionValue = string | number | boolean;
type CellTypeFormatOptionValues = Partial<Record<FormatOptions, CellTypeFormatOptionValue>>;

const getSharedCellTypeName = (selectedCellsTypes: Array<CellTypeObject | undefined>): CellTypeNames | undefined => {
    const sharedCellTypeName = selectedCellsTypes[0]?.name || CellTypeNames.AUTO;
    const hasMultipleCellTypes = selectedCellsTypes.some(
        (typeObject) => (typeObject?.name || CellTypeNames.AUTO) !== sharedCellTypeName,
    );

    return hasMultipleCellTypes ? undefined : sharedCellTypeName;
};

export const getDefaultTypeObject = <T extends CellTypeObject>(cellTypeName: CellTypeNames): T => {
    const { formattingOptions = {} } = CellTypeConstants[cellTypeName];
    const defaultOptions: { [key: string]: number | boolean | string | null } = { name: cellTypeName };

    // Loop through the formatting options and set the default value for each
    (Object.keys(formattingOptions) as Array<FormatOptions>).forEach((option) => {
        const value = formattingOptions[option]?.defaultValue;
        if (value === undefined) return;
        defaultOptions[option] = value;
    });
    // Necessary to cast to CellTypeObject object here because it will only match the type after the foreach loop
    return defaultOptions as T;
};

export const cellCanBeRichFormatted = (cellData: CellData): boolean => {
    return !isFormulaCell(cellData) && (!cellData.type || cellData.type.name === CellTypeNames.TEXT);
};

/**
 * Returns whether a cell should be treated as read only in the grid.
 */
export const cellIsReadOnly = (cellData: CellData | undefined): boolean => {
    if (!cellData || !cellData.type) return false;

    return CellTypeConstants[cellData.type.name]?.allowCellEditing === false && !isFormula(cellData.value);
};

/**
 * Returns the selected cells' shared cell type with format options that they share.
 * If selected cells have different cell types, return undefined.
 */
export const getSharedCellTypeFormatOptions = (
    selectedCellsTypes: Array<CellTypeObject | undefined>,
): CellTypeObjectWithAuto | undefined => {
    if (selectedCellsTypes.length === 0) return;

    const sharedCellTypeName = getSharedCellTypeName(selectedCellsTypes);
    if (!sharedCellTypeName) return;

    if (sharedCellTypeName === CellTypeNames.AUTO) return { name: CellTypeNames.AUTO };

    const sharedCellFormat = getDefaultTypeObject(sharedCellTypeName);
    const cellTypeFormattingOptions = CellTypeConstants[sharedCellTypeName].formattingOptions;

    if (!cellTypeFormattingOptions) return sharedCellFormat;

    const cellTypesWithOptions = selectedCellsTypes as Array<CellTypeFormatOptionValues | undefined>;

    (Object.keys(cellTypeFormattingOptions) as Array<FormatOptions>).forEach((formatOption) => {
        const firstValue = cellTypesWithOptions[0]?.[formatOption];
        if (isNil(firstValue)) return;

        const isSharedValue = cellTypesWithOptions.every((typeObject) => typeObject?.[formatOption] === firstValue);

        if (!isSharedValue) return;

        (sharedCellFormat as CellTypeFormatOptionValues)[formatOption] = firstValue;
    });

    return sharedCellFormat;
};
